import React, { useState, useEffect, useMemo, useCallback } from 'react';
import api from '../services/api.js';

const AccountingPage = () => {
    const [deliveries, setDeliveries] = useState([]);
    const [investments, setInvestments] = useState([]);
    const [withdrawals, setWithdrawals] = useState([]);
    const [panelsList, setPanelsList] = useState([]);
    const [usersList, setUsersList] = useState([]);
    const [apiKeys, setApiKeys] = useState([]); // firma oranı için
    // Firma (API key) bazlı muhasebe
    const [firmRows, setFirmRows] = useState([]);
    const [firmDayOffset, setFirmDayOffset] = useState(0);
    const fmtN = (n) => Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

    // Varsayılan tarih: Bugün
    const today = new Date().toISOString().split('T')[0];
    const [filters, setFilters] = useState({
        panelId: '',
        startDate: today,
        endDate: today
    });

    const fetchData = useCallback(async () => {
        try {
            const [delRes, invRes, withRes, panelsRes, usersRes, keysRes] = await Promise.all([
                api.getDeliveries(),
                api.getInvestments({ status: 'approved' }),
                api.getWithdrawals({ status: 'approved' }),
                api.getPanels(),
                api.getUsers(),
                api.getApiKeys()
            ]);

            setDeliveries(delRes.data);
            setInvestments(invRes.data);
            setWithdrawals(withRes.data);
            setPanelsList(panelsRes.data);
            setUsersList(usersRes.data);
            setApiKeys(keysRes.data);
        } catch (error) {
            console.error('Veri çekme hatası:', error);
        }
    }, []);

    useEffect(() => {
        document.title = 'PayzoPay - Muhasebe';
        fetchData();
        const interval = setInterval(fetchData, 10000);
        return () => clearInterval(interval);
    }, [fetchData]);

    const fetchFirms = useCallback(async () => {
        try {
            const res = await api.getFirmAccounting({ dayOffset: firmDayOffset });
            setFirmRows(res.data);
        } catch (error) {
            console.error('Firma muhasebesi çekilemedi:', error);
        }
    }, [firmDayOffset]);

    useEffect(() => {
        fetchFirms();
        const iv = setInterval(fetchFirms, 15000);
        return () => clearInterval(iv);
    }, [fetchFirms]);

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({ ...prev, [name]: value }));
    };

    const filterData = useCallback((data, dateField) => {
        return data.filter(item => {
            const itemDate = new Date(item[dateField]);
            const start = filters.startDate ? new Date(filters.startDate) : null;
            const end = filters.endDate ? new Date(filters.endDate) : null;

            if (start) start.setHours(0, 0, 0, 0);
            if (end) end.setHours(23, 59, 59, 999);

            const matchesDate = (!start || itemDate >= start) && (!end || itemDate <= end);

            const matchesPanel = !filters.panelId || item.panelId === filters.panelId;

            return matchesDate && matchesPanel;
        });
    }, [filters]);

    const filteredDeliveries = useMemo(() => filterData(deliveries, 'createdAt'), [deliveries, filterData]);
    const filteredInvestments = useMemo(() => filterData(investments, 'createdAt'), [investments, filterData]);
    const filteredWithdrawals = useMemo(() => filterData(withdrawals, 'createdAt'), [withdrawals, filterData]);

    const updateDeliveryStatus = async (id, status) => {
        if (!window.confirm(`Bu teslimatı ${status === 'Tamamlandı' ? 'onaylamak' : 'reddetmek'} istediğinize emin misiniz?`)) return;
        try {
            await api.updateDeliveryStatus(id, status);
            fetchData();
        } catch (error) {
            alert('İşlem başarısız.');
        }
    };

    // panelId -> firma (API key) komisyon oranı
    const firmaRateByPanel = useMemo(() => {
        const map = {};
        apiKeys.forEach(k => {
            const rate = Number(k.commissionRate) || 0;
            const pids = (Array.isArray(k.panelIds) && k.panelIds.length) ? k.panelIds : (k.dealerAdminId ? [k.dealerAdminId] : []);
            pids.forEach(pid => { map[pid] = rate; });
        });
        return map;
    }, [apiKeys]);

    const stats = useMemo(() => {
        const totalInvestment = filteredInvestments.reduce((acc, curr) => acc + (curr.amount || 0), 0);
        const totalWithdrawal = filteredWithdrawals.reduce((acc, curr) => acc + (curr.amount || 0), 0);
        const totalDelivery = filteredDeliveries.reduce((acc, curr) => {
            return curr.status === 'Tamamlandı' ? acc + (curr.tlAmount || 0) : acc;
        }, 0);

        // BİZİM komisyon = yatırım × (firma oranı − panel oranı) / 100  (net komisyon)
        const totalCommission = filteredInvestments.reduce((acc, curr) => {
            const panel = panelsList.find(p => p.panelId === curr.panelId);
            const panelRate = panel && panel.commissionRate ? parseFloat(panel.commissionRate) : 0;
            const firmaRate = firmaRateByPanel[curr.panelId];
            if (firmaRate === undefined) return acc; // firma bilinmiyorsa katkı yok
            return acc + ((curr.amount || 0) * (firmaRate - panelRate) / 100);
        }, 0);

        const net = totalInvestment - totalWithdrawal;

        return { totalInvestment, totalWithdrawal, totalDelivery, totalCommission, net };
    }, [filteredInvestments, filteredWithdrawals, filteredDeliveries, panelsList, firmaRateByPanel]);

    const cardContainerStyle = {
        display: 'flex',
        gap: '20px',
        marginBottom: '20px',
        flexWrap: 'wrap'
    };

    const cardStyle = (borderColor) => ({
        backgroundColor: 'var(--card-bg)',
        borderRadius: '8px',
        boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
        padding: '20px',
        borderLeft: `5px solid ${borderColor}`,
        flex: 1,
        minWidth: '200px',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center'
    });

    const titleStyle = {
        margin: '0 0 10px 0',
        color: '#6c757d',
        fontSize: '0.9rem',
        fontWeight: 'bold',
        textTransform: 'uppercase'
    };

    const amountStyle = {
        margin: 0,
        fontSize: '1.8rem',
        fontWeight: 'bold',
        color: 'var(--text-color)'
    };

    const labelStyle = { fontWeight: 'bold', fontSize: '0.9em', marginBottom: '5px', display: 'block', color: 'var(--text-color)' };
    const inputStyle = { width: '100%', padding: '8px', border: '1px solid var(--input-border)', borderRadius: '4px', boxSizing: 'border-box', backgroundColor: 'var(--input-bg)', color: 'var(--text-color)' };

    const getStatusRowBackground = (status) => {
        switch (status) {
            case 'Bekliyor': return 'rgba(255, 206, 84, 0.5)'; // Pastel Sarı
            case 'Tamamlandı': return 'rgba(160, 212, 104, 0.5)'; // Pastel Yeşil
            case 'Reddedildi': return 'rgba(237, 85, 101, 0.5)'; // Pastel Kırmızı
            default: return 'var(--table-row-even)';
        }
    };

    return (
        <div style={{ padding: '20px' }}>
            <h2>Muhasebe</h2>

            {/* Firma (API key) bazlı muhasebe — her firma ayrı, devir ile birlikte */}
            <div style={{ backgroundColor: 'var(--card-bg)', padding: '20px', borderRadius: '8px', marginBottom: '20px', boxShadow: 'var(--shadow)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px', flexWrap: 'wrap', gap: '10px' }}>
                    <h3 style={{ margin: 0 }}>Firma Muhasebesi</h3>
                    <div>
                        <label style={{ ...labelStyle, display: 'inline', marginRight: '8px' }}>Gün:</label>
                        <select value={firmDayOffset} onChange={(e) => setFirmDayOffset(parseInt(e.target.value))} style={{ ...inputStyle, width: 'auto', display: 'inline-block' }}>
                            <option value={0}>Bugün</option>
                            <option value={1}>Dün</option>
                            <option value={2}>2 gün önce</option>
                        </select>
                    </div>
                </div>
                <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', color: 'var(--text-color)', fontSize: '0.9em' }}>
                        <thead>
                            <tr style={{ textAlign: 'right', borderBottom: '2px solid var(--border-color)' }}>
                                <th style={{ padding: '8px', textAlign: 'left' }}>Firma</th>
                                <th style={{ padding: '8px', textAlign: 'right' }}>Devir</th>
                                <th style={{ padding: '8px', textAlign: 'right' }}>Yatırım</th>
                                <th style={{ padding: '8px', textAlign: 'right' }}>Çekim</th>
                                <th style={{ padding: '8px', textAlign: 'right' }}>Teslim</th>
                                <th style={{ padding: '8px', textAlign: 'right' }}>Panel Kom.</th>
                                <th style={{ padding: '8px', textAlign: 'right' }}>Brüt Kom. (API/Toplam)</th>
                                <th style={{ padding: '8px', textAlign: 'right' }}>Bizim Kom. (Net)</th>
                                <th style={{ padding: '8px', textAlign: 'right', fontWeight: 'bold' }}>Kalan Kasa</th>
                            </tr>
                        </thead>
                        <tbody>
                            {firmRows.length === 0 ? (
                                <tr><td colSpan="9" style={{ padding: '12px', textAlign: 'center', opacity: 0.7 }}>Firma (API key) bulunamadı.</td></tr>
                            ) : firmRows.map(f => (
                                <tr key={f.firmaId} style={{ borderBottom: '1px solid var(--border-color)' }}>
                                    <td style={{ padding: '8px', textAlign: 'left', fontWeight: 'bold' }}>{f.firma} <span style={{ opacity: 0.6, fontWeight: 'normal' }}>(%{f.commissionRate})</span></td>
                                    <td style={{ padding: '8px', textAlign: 'right' }}>{fmtN(f.devir)}</td>
                                    <td style={{ padding: '8px', textAlign: 'right' }}>{fmtN(f.yatirim)}</td>
                                    <td style={{ padding: '8px', textAlign: 'right' }}>{fmtN(f.cekim)}</td>
                                    <td style={{ padding: '8px', textAlign: 'right' }}>{fmtN(f.teslim)}</td>
                                    <td style={{ padding: '8px', textAlign: 'right' }}>{fmtN(f.panelKomisyon)}</td>
                                    <td style={{ padding: '8px', textAlign: 'right' }}>{fmtN(f.brutKomisyon)}</td>
                                    <td style={{ padding: '8px', textAlign: 'right', color: f.netKomisyon >= 0 ? '#28a745' : '#dc3545' }}>{fmtN(f.netKomisyon)}</td>
                                    <td style={{ padding: '8px', textAlign: 'right', fontWeight: 'bold' }}>{fmtN(f.kalanKasa)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                <p style={{ fontSize: '0.8em', opacity: 0.7, marginTop: '10px' }}>Kalan Kasa = Devir + Yatırım − Çekim − Teslim − Brüt Komisyon (API). Devir = bir önceki günün neti. Bizim Komisyon (Net) = Brüt (API/firma) − Departman komisyonu. Yatırım/çekim/teslim, firmanın tüm departmanlarını kapsar (elle eklenenler dahil).</p>
            </div>

            {/* Filtreler */}
            <div style={{ backgroundColor: 'var(--card-bg)', padding: '20px', borderRadius: '8px', marginBottom: '20px', boxShadow: 'var(--shadow)' }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '15px' }}>
                    <div>
                        <label style={labelStyle}>Panel</label>
                        <select name="panelId" value={filters.panelId} onChange={handleFilterChange} style={inputStyle}>
                            <option value="">Hepsi</option>
                            {panelsList.map(p => (
                                <option key={p._id} value={p.panelId}>{p.name || p.panelId}</option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label style={labelStyle}>Başlangıç Tarihi</label>
                        <input type="date" name="startDate" value={filters.startDate} onChange={handleFilterChange} style={inputStyle} />
                    </div>
                    <div>
                        <label style={labelStyle}>Bitiş Tarihi</label>
                        <input type="date" name="endDate" value={filters.endDate} onChange={handleFilterChange} style={inputStyle} />
                    </div>
                </div>
            </div>

            {/* İstatistik Kartları */}
            <div style={cardContainerStyle}>
                <div style={cardStyle('#28a745')}>
                    <h5 style={titleStyle}>Toplam Yatırım</h5>
                    <p style={amountStyle}>{stats.totalInvestment.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                </div>
                <div style={cardStyle('#dc3545')}>
                    <h5 style={titleStyle}>Toplam Çekim</h5>
                    <p style={amountStyle}>{stats.totalWithdrawal.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                </div>
                <div style={cardStyle(stats.net >= 0 ? '#28a745' : '#dc3545')}>
                    <h5 style={titleStyle}>Net</h5>
                    <p style={{ ...amountStyle, color: stats.net >= 0 ? '#28a745' : '#dc3545' }}>
                        {stats.net.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </p>
                </div>
                <div style={cardStyle('#ffc107')}>
                    <h5 style={titleStyle}>Toplam Teslim</h5>
                    <p style={amountStyle}>{stats.totalDelivery.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                </div>
                <div style={cardStyle('#007bff')}>
                    <h5 style={titleStyle}>Toplam Komisyon (Bizim / Net)</h5>
                    <p style={amountStyle}>{stats.totalCommission.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                </div>
            </div>

            {/* Teslimat Listesi Tablosu */}
            <h3 style={{ color: 'var(--text-color)', marginBottom: '15px' }}>Teslimat Listesi</h3>
            <div style={{ backgroundColor: 'var(--card-bg)', borderRadius: '15px', boxShadow: 'var(--shadow)', overflow: 'hidden', padding: '20px' }}>
                <table cellPadding="15" style={{ width: '100%', borderCollapse: 'separate', borderSpacing: '0 10px', color: 'var(--text-color)' }}>
                    <thead>
                        <tr style={{ backgroundColor: 'var(--table-header-bg)', borderBottom: '2px solid var(--border-color)', textAlign: 'left' }}>
                            <th>ID</th>
                            <th>TXID</th>
                            <th>Açıklama</th>
                            <th>Kur(USDT/TL)</th>
                            <th>Miktar(USDT)</th>
                            <th>Miktar (TL)</th>
                            <th>Durum</th>
                            <th>Kullanıcı</th>
                            <th>Eklenme Zamanı</th>
                            <th>Firma</th>
                            <th>İşlemler</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredDeliveries.length === 0 ? (
                            <tr><td colSpan="11" style={{ textAlign: 'center' }}>Veri bulunamadı.</td></tr>
                        ) : (
                            filteredDeliveries.map(item => {
                                const rowBg = getStatusRowBackground(item.status);
                                return (
                                    <tr key={item._id}>
                                        <td style={{ backgroundColor: rowBg }}>{item._id}</td>
                                        <td style={{ backgroundColor: rowBg }}>{item.txid}</td>
                                        <td style={{ backgroundColor: rowBg }}>{item.description}</td>
                                        <td style={{ backgroundColor: rowBg }}>{item.usdtRate}</td>
                                        <td style={{ backgroundColor: rowBg }}>{Number(item.usdtAmount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                                        <td style={{ backgroundColor: rowBg }}>{Number(item.tlAmount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                                        <td style={{ backgroundColor: rowBg }}>{item.status}</td>
                                        <td style={{ backgroundColor: rowBg }}>{item.user}</td>
                                        <td style={{ backgroundColor: rowBg }}>{new Date(item.createdAt).toLocaleString()}</td>
                                        <td style={{ backgroundColor: rowBg }}>{item.company}</td>
                                        <td style={{ backgroundColor: rowBg }}>
                                            {item.status === 'Bekliyor' && (
                                                <div style={{ display: 'flex', width: '100%', gap: '5px' }}>
                                                    <button onClick={() => updateDeliveryStatus(item._id, 'Tamamlandı')} style={{ flex: 1, padding: '8px 0', backgroundColor: '#28a745', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold' }}>Onayla</button>
                                                    <button onClick={() => updateDeliveryStatus(item._id, 'Reddedildi')} style={{ flex: 1, padding: '8px 0', backgroundColor: '#dc3545', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold' }}>Reddet</button>
                                                </div>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default AccountingPage;